import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Check, X, LogIn, Loader2 } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

/**
 * 🎬 项目组邀请接受页面
 * /join/:token
 * 重新设计：以项目海报为主视觉，邀请信息覆盖在海报上方
 */
export default function JoinCrewPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    
    const [loading, setLoading] = useState(true);
    const [joining, setJoining] = useState(false);
    const [invitation, setInvitation] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [alreadyMember, setAlreadyMember] = useState(false);
    
    // 验证邀请链接
    useEffect(() => {
        const verifyInvitation = async () => {
            try {
                const data = await api.get(`/api/invitations/${token}`);
                if (data.valid) {
                    setInvitation({
                        crew_name: data.crew?.name,
                        crew_poster: data.crew?.poster || null,
                        inviter_name: data.inviter?.username
                    });
                } else {
                    setError(data.error || '邀请链接无效');
                }
            } catch (e) {
                console.error('验证邀请失败:', e);
                setError('邀请链接无效或已过期');
            } finally {
                setLoading(false);
            }
        };
        
        if (token) {
            verifyInvitation();
        }
    }, [token]);
    
    // 接受邀请
    const handleJoin = async () => {
        if (!isAuthenticated) {
            localStorage.setItem('pendingCrewInvite', token);
            navigate('/login');
            return;
        }
        
        try {
            setJoining(true);
            const data = await api.post(`/api/invitations/${token}/accept`);
            if (data.success) {
                if (data.alreadyMember) {
                    setAlreadyMember(true);
                    setInvitation(prev => ({ ...prev, crew_name: data.crewName }));
                } else {
                    setSuccess(true);
                }
                setTimeout(() => { navigate('/'); }, 3000);
            } else {
                setError(data.error || '加入失败');
            }
        } catch (e) {
            console.error('加入项目组失败:', e);
            setError(e.message || '加入失败，请重试');
        } finally {
            setJoining(false);
        }
    };

    // 海报 URL
    const posterUrl = invitation?.crew_poster;
    
    return (
        <div style={{
            width: '100vw', height: '100vh',
            background: '#000',
            display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: '#fff',
            overflow: 'hidden'
        }}>
            {/* 背景海报（模糊 + 暗化，仅作氛围） */}
            {posterUrl && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 0,
                    backgroundImage: `url(${posterUrl})`,
                    backgroundSize: 'cover', backgroundPosition: 'center',
                    filter: 'blur(40px) brightness(0.3)',
                    transform: 'scale(1.1)'
                }} />
            )}

            {/* 主内容卡片 */}
            <div style={{
                position: 'relative', zIndex: 1,
                width: 360, maxWidth: '90vw',
                display: 'flex', flexDirection: 'column', alignItems: 'center'
            }}>
                {loading ? (
                    <div style={{ padding: '80px 0', textAlign: 'center' }}>
                        <Loader2 size={36} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
                        <p style={{ marginTop: 16, color: '#71717a', fontSize: 13 }}>验证邀请中...</p>
                    </div>
                ) : error ? (
                    /* 错误状态 */
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'rgba(239,68,68,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <X size={28} style={{ color: '#f87171' }} />
                        </div>
                        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>邀请无效</h2>
                        <p style={{ fontSize: 13, color: '#71717a', marginBottom: 24 }}>{error}</p>
                        <Link to="/" style={{
                            display: 'inline-block', padding: '8px 20px', borderRadius: 8,
                            background: 'rgba(255,255,255,0.08)', color: '#a1a1aa', fontSize: 13,
                            textDecoration: 'none'
                        }}>返回首页</Link>
                    </div>
                ) : alreadyMember ? (
                    /* 已是成员 */
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'rgba(59,130,246,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <Check size={28} style={{ color: '#3b82f6' }} />
                        </div>
                        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>你已是成员</h2>
                        <p style={{ fontSize: 13, color: '#71717a', marginBottom: 8 }}>
                            你已经是 <span style={{ color: '#3b82f6' }}>{invitation?.crew_name}</span> 的成员了
                        </p>
                        <p style={{ fontSize: 12, color: '#52525b' }}>正在跳转到首页...</p>
                    </div>
                ) : success ? (
                    /* 成功状态 */
                    <div style={{ textAlign: 'center', padding: '60px 0' }}>
                        <div style={{
                            width: 56, height: 56, borderRadius: '50%',
                            background: 'rgba(34,197,94,0.15)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <Check size={28} style={{ color: '#22c55e' }} />
                        </div>
                        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>欢迎加入！</h2>
                        <p style={{ fontSize: 13, color: '#71717a', marginBottom: 8 }}>
                            你已成功加入 <span style={{ color: '#3b82f6' }}>{invitation?.crew_name}</span>
                        </p>
                        <p style={{ fontSize: 12, color: '#52525b' }}>正在跳转...</p>
                    </div>
                ) : (
                    /* 邀请信息：海报 + 文字 */
                    <>
                        {/* 海报图（纯净展示，无文字遮罩） */}
                        <div style={{
                            width: '100%', aspectRatio: '9 / 16',
                            borderRadius: 12, overflow: 'hidden',
                            background: '#18181b',
                            border: '1px solid rgba(255,255,255,0.08)',
                            marginBottom: 16,
                        }}>
                            {posterUrl ? (
                                <img
                                    src={posterUrl}
                                    alt="项目海报"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                />
                            ) : (
                                /* 无海报占位 */
                                <div style={{
                                    width: '100%', height: '100%',
                                    display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center',
                                    background: 'linear-gradient(180deg, #1c1c2e 0%, #18181b 100%)'
                                }}>
                                    <div style={{ fontSize: 52, marginBottom: 10, opacity: 0.25 }}>🎬</div>
                                </div>
                            )}
                        </div>

                        {/* 邀请信息卡片（白底，黑灰文字） */}
                        <div style={{
                            width: '100%',
                            background: '#fff',
                            borderRadius: 12,
                            padding: '14px 16px',
                            marginBottom: 10,
                            boxSizing: 'border-box'
                        }}>
                            {/* 邀请语 */}
                            <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 8px 0', lineHeight: 1.5 }}>
                                <span style={{ color: '#111', fontWeight: 600 }}>{invitation?.inviter_name}</span>
                                {' '}邀请你加入{' '}
                                <span style={{ color: '#374151', fontWeight: 500 }}>「{invitation?.crew_name}」</span>
                                {' '}共同创作
                            </p>
                            <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 10px 0', lineHeight: 1.5 }}>
                                加入后你将成为{' '}
                                <span style={{ color: '#111', fontWeight: 600 }}>「设计师」</span>
                                {' '}角色，有以下权限：
                            </p>

                            {/* 分割线 */}
                            <div style={{ height: 1, background: '#f3f4f6', marginBottom: 10 }} />

                            {/* 权限列表 */}
                            <div style={{ color: '#6b7280', fontSize: 12, lineHeight: 1 }}>
                                {[
                                    '查看和下载所有项目素材',
                                    '上传和管理媒体文件',
                                    '参与画布创作与编辑',
                                    '邀请其他成员加入项目组',
                                ].map((item, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < 3 ? 7 : 0 }}>
                                        <span style={{
                                            display: 'inline-block', flexShrink: 0,
                                            width: 7, height: 7, borderRadius: '50%',
                                            border: '1.5px solid #9ca3af'
                                        }} />
                                        <span>{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 按钮 */}
                        {isAuthenticated ? (
                            <button
                                onClick={handleJoin}
                                disabled={joining}
                                style={{
                                    width: '100%',
                                    padding: '11px 24px', borderRadius: 10,
                                    background: joining ? 'rgba(239,68,68,0.4)' : '#ef4444',
                                    border: 'none',
                                    color: '#fff', fontSize: 14, fontWeight: 500,
                                    cursor: joining ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                    transition: 'all 0.2s',
                                    boxShadow: joining ? 'none' : '0 4px 16px rgba(239,68,68,0.35)'
                                }}
                            >
                                {joining ? (
                                    <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />加入中...</>
                                ) : (
                                    <><Check size={16} />接受邀请</>
                                )}
                            </button>
                        ) : (
                            <button
                                onClick={handleJoin}
                                style={{
                                    width: '100%',
                                    padding: '11px 24px', borderRadius: 10,
                                    background: '#ef4444',
                                    border: 'none',
                                    color: '#fff', fontSize: 14, fontWeight: 500,
                                    cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 16px rgba(239,68,68,0.35)'
                                }}
                            >
                                <LogIn size={16} />登录后加入
                            </button>
                        )}
                    </>
                )}
            </div>
            
            <style>{`
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
